import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ENDPOINTS } from 'config/endpoints';
import CronoError from 'crono-fe-common/types/error';
import { EventOutcome } from 'crono-fe-common/types/event';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import { invalidateActivitiesQueries } from 'hooks/services/event/useActivitiesSearch';
import { invalidateTimelineQueries } from 'hooks/services/timeline/useSearchTimeline';
import client from 'utils/clients/client';

const ENDPOINT = ENDPOINTS.events.event;

export interface PatchEventPayload {
  eventId: number;
  title?: string;
  startTime?: string;
  endTime?: string | null;
  // A null `endTime` reads as "not part of this patch", so removing an end time
  // is asked for with this flag instead.
  clearEndTime?: boolean;
  isAllDay?: boolean;
  location?: string;
  description?: string;
  conferenceLink?: string;
  attendeeEmails?: string[];
  outcome?: EventOutcome;
}

const patchEvent = (payload: PatchEventPayload) => {
  const request: Request = {
    url: ENDPOINT,
    config: {
      method: 'patch',
      data: { data: payload },
    },
  };
  return client(request);
};

export const usePatchEvent = () => {
  const queryClient = useQueryClient();

  return useMutation<Response<null>, CronoError, PatchEventPayload>(
    patchEvent,
    {
      onSuccess: (_, variables) => {
        invalidateActivitiesQueries(queryClient);
        invalidateTimelineQueries(queryClient);
        // Returned so the mutation stays pending until the patched event has
        // been refetched.
        return queryClient.invalidateQueries({
          queryKey: [ENDPOINT, variables.eventId],
        });
      },
    },
  );
};
